import { call, put, select } from 'redux-saga/effects';
import { PRODUCTS_COMPARE_MANAGE } from '../../actions';
import { UnifiedCompareState, ProductErrorState, ApiError } from '../../types';
import { createList } from '../../api';
import { transformCompareListsToAPI } from '../../utils';
import { ApiCancelToken } from 'api';
import { updateCompareState, isDataSyncError, handleBadRequestError } from './utils';
import { getUnifiedCompareData } from '../../selectors/base';

const ERROR_MESSAGES = {
  CREATE_LIST_ERROR: 'Ошибка создания списка',
} as const;

/**
 * NOTE: Сага для создания нового списка сравнения
 * Создает список на сервере и обновляет локальное состояние
 */
export function* createListSaga(params: {
  customerId: string;
  host: string;
  abortController: { token: ApiCancelToken };
  currentLists: ReturnType<typeof transformCompareListsToAPI>;
  productId: string;
  list: { name: string; groupId: string; products: string[] };
}): Generator {
  const currentState: UnifiedCompareState = yield select(getUnifiedCompareData);

  try {
    const { customerId, host, abortController, list } = params;

    const newProductsData = list.products.reduce(
      (acc, prodId) => {
        acc[prodId] = { productId: prodId };
        return acc;
      },
      {} as Record<string, { productId: string }>,
    );

    const updatedState = updateCompareState(currentState, list.groupId, {
      name: list.name,
      groupId: list.groupId,
      productsData: newProductsData,
      isDataLoaded: false,
      isSyncedWithServer: true,
    });

    yield put(PRODUCTS_COMPARE_MANAGE.success(updatedState));

    const body = {
      customerId,
      compareList: {
        name: list.name,
        groupId: list.groupId,
        products: list.products,
      },
    };

    yield call(createList, body, host, abortController.token);
  } catch (error) {
    const err = error instanceof Error ? error : new Error(ERROR_MESSAGES.CREATE_LIST_ERROR);

    if (isDataSyncError(error as ApiError)) {
      yield* handleBadRequestError();
    }

    const errorState: ProductErrorState = {
      error: err,
      productId: params.productId,
      groupId: params.list.groupId,
      currentState,
    };

    yield put(PRODUCTS_COMPARE_MANAGE.failure(errorState));
  }
}
